import React, { useContext, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import MobileBottomDrawer from 'components/MobileBottomDrawer';
import MaxWidthContainer from 'components/MaxWidthContainer';
import { Inbox } from '@talkjs/react';
import { useTheme } from '@emotion/react';
import { isMobile } from 'helpers';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { chatActions, chatSelectors } from 'redux/chat';
import { ChatContext } from 'providers/ChatProvider';
import Grid from 'components/Grid';
import Button from '@mui/material/Button';
import CreateRoundedIcon from '@mui/icons-material/CreateRounded';
import ArrowBackIosRoundedIcon from '@mui/icons-material/ArrowBackIosRounded';
import styled from '@emotion/styled/macro';
import useMediaQuery from '@mui/material/useMediaQuery';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import PropTypes from 'prop-types';
import CreateConversationForm from './CreateConversationForm';

const DrawerContent = styled('div')`
  height: 100%;

  margin-top: 1rem;

  padding-left: ${({ theme }) => theme.spacing(1)};
  padding-right: ${({ theme }) => theme.spacing(1)};
`;

const Wrapper = styled('div')`
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const Top = styled('div')`
  margin-bottom: ${({ theme }) => theme.spacing(1)};
`;

const Bottom = styled('div')`
  height: 100%;
`;


const ChatBottomDrawer = ({ open, onClose, onOpen, ...rest }) => {

  const theme = useTheme();
  const dispatch = useDispatch();
  const { chatReady, onManageMembers, onManageConversation } = useContext(ChatContext);
  const { t } = useTranslation('chat');

  // Configured on TalkJS. Not easily configurable.
  const twoColumnLayout = useMediaQuery('(min-width: 640px)');

  const [mode, setMode] = useState('view');

  const [conversationId, setConversationId] = useState(null);

  // Unmounting the Inbox destroys its iframe and rejects TalkJS calls still in flight.
  const [keepMounted, setKeepMounted] = useState(false);
  useEffect(() => {
    if (open) {
      setKeepMounted(true);
    }
  }, [open]);

  const onDrawerClose = () => {
    // TalkJS marks messages read while a conversation is shown, even in a closed drawer.
    setConversationId(null);
    onClose();
  };

  const getConversationSelector = useSelector(chatSelectors.getConversationSelector);

  useEffect(() => {
    if (!conversationId || !!getConversationSelector(conversationId)) {
      return;
    }
    dispatch(chatActions.getConversation({ conversationId }));
  }, [dispatch, getConversationSelector, conversationId]);

  const onCreateSuccess = ({ data: $conversationId }) => {
    setMode('view');
    setConversationId($conversationId);
  };

  // Global code to make the chat openable from push notifications. Order matters!
  useLayoutEffect(() => {
    if (!chatReady) {
      return; // To fix initial incorrect handling
    }
    // 1. Set up global function
    window.openGuavaConversation = ($conversationId) => {
      onOpen();
      setMode('view');
      setConversationId($conversationId);
    };
    // 2. Trigger event
    window.dispatchEvent(new CustomEvent('chatReady'));
  }, [chatReady, onOpen]);

  const inboxTheme = useMemo(() => ({
    name: 'guavahr',
    custom: {
      primaryColor: theme.palette.primary.main,
      primaryColorDark: theme.palette.primary.dark,
      primaryColorLight: theme.palette.primary.light,
      primaryContrastText: theme.palette.primary.contrastText,

      // Constant across conversations: a theme change makes @talkjs/react recreate the Inbox.
      displayManageMembers: 'block', // display: none|block;
      displayManageConversation: 'none', // display: none|block;
      displayToggleSize: isMobile ? 'none' : 'block', // display: none|block;
      displayMinimize: isMobile ? 'none' : 'flex', // display: none|flex
      displayBack: isMobile && !twoColumnLayout ? 'block' : 'none', // display: none|block

      chatboxBorderRadius: 0,

      // IMPORTANT! Translations need quotes around the strings!
      viewMembersTranslation: `"${t('actionMenu.viewMembers')}"`,
      toggleSizeTranslation: `"${t('actionMenu.toggleSize')}"`,
      manageConversationTranslation: `"${t('actionMenu.manageConversation')}"`,
    },
  }), [t, theme.palette.primary.contrastText, theme.palette.primary.dark, theme.palette.primary.light, theme.palette.primary.main, twoColumnLayout]);

  const onConversationSelected = e => {
    setConversationId(e?.conversation?.id ?? null);
  };

  const onMarkConversationAsUnread = (e) => {
    if (!e.conversation?.id) {
      e?.preventDefault();
      return false;
    }
    dispatch(chatActions.markConversationUnread({ conversationId: e.conversation.id }));
    setConversationId(null);
  };

  const onCustomConversationAction = (e) => {
    if (e.action === 'view_members') {
      onManageMembers(e.conversation.id);
    }
    if (e.action === 'manage_conversation') {
      onManageConversation(e.conversation.id);
    }
    if (e.action === 'back') {
      setMode('view');
      setConversationId(null);
    }
  };

  const onCreateClick = () => {
    setMode('create');
    setConversationId(null);
  };

  const onViewClick = () => {
    setMode('view');
  };

  return (
    <MobileBottomDrawer
      disableHandlebarPadding={!!conversationId && !twoColumnLayout}
      ModalProps={{
        keepMounted,
        closeAfterTransition: true,
        sx: {
          zIndex: theme.zIndex.chatBackdrop,
        },
      }}
      PaperProps={{
        className: 'u-safe-bottom-padding-tight',
        sx: {
          zIndex: theme.zIndex.chatDrawer,

          height: '100%',
          maxHeight: 'calc(100vh - 20px - var(--safe-area-inset-top))',
          // borderTopRightRadius: 0,
          // borderTopLeftRadius: 0,
        },
      }}

      open={open}
      onOpen={onOpen}
      onClose={onDrawerClose}
      {...rest}
    >
      <Wrapper>

        {(!conversationId || twoColumnLayout) && (
          <Top>
            <MaxWidthContainer>
              <Grid container direction="row" wrap="nowrap" justifyContent="space-between" alignItems="center">
                <Grid item>
                  {mode === 'create' && (
                    <Button variant="text" size="small" startIcon={<ArrowBackIosRoundedIcon />} edge="start" onClick={onViewClick}>{t('chat:drawer.back')}</Button>
                  )}
                  {mode === 'view' && (
                    <Button variant="text" size="small" startIcon={<CloseRoundedIcon />} edge="start" onClick={onDrawerClose}>{t('chat:drawer.close')}</Button>
                  )}
                </Grid>
                <Grid item>
                  {mode === 'view' && (
                    <Button variant="text" size="small" startIcon={<CreateRoundedIcon />} edge="end" onClick={onCreateClick}>{t('chat:drawer.createNewChat')}</Button>
                  )}
                </Grid>
              </Grid>
            </MaxWidthContainer>
          </Top>
        )}

        <Bottom>
          <MaxWidthContainer sx={{ height: '100%' }} disableGutters={!!conversationId || !twoColumnLayout}>
            {mode === 'view'
              ? (
                <Inbox
                  showChatHeader
                  showFeedHeader={false}
                  showMobileBackButton={false}

                  conversationId={conversationId}
                  onConversationSelected={onConversationSelected}
                  onMarkConversationAsUnread={onMarkConversationAsUnread}
                  onCustomConversationAction={onCustomConversationAction}

                  theme={inboxTheme}

                  style={{ height: '100%', width: '100%' }}
                />
              )
              : (
                <DrawerContent>
                  <CreateConversationForm
                    onSuccess={onCreateSuccess}
                  />
                </DrawerContent>
              )}
          </MaxWidthContainer>
        </Bottom>
      </Wrapper>
    </MobileBottomDrawer>
  );
};

ChatBottomDrawer.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onOpen: PropTypes.func.isRequired,
};

export default ChatBottomDrawer;
